import React, { useState, useEffect } from "react";
import { Box, Typography, Container, Stack } from "@mui/material";
import { motion, useAnimation } from "framer-motion";
import { Stethoscope, HeartPulse, Activity, Users, Award, TrendingUp } from "lucide-react";
import FloatingElement from "../components/FloatingElement";
import { fadeInUp } from "../utils/animations";

// Animated Wave Background Component
const AnimatedWaves = () => {
  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        opacity: 0.6,
      }}
    >
      {/* Wave 1 */}
      <Box
        component={motion.svg}
        animate={{
          x: [0, -50, 0],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: "linear",
        }}
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "200%",
          height: "100%",
        }}
        viewBox="0 0 1440 320"
        preserveAspectRatio="none"
      >
        <path
          fill="rgba(255, 255, 255, 0.05)"
          d="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,122.7C672,117,768,139,864,144C960,149,1056,139,1152,122.7C1248,107,1344,85,1392,74.7L1440,64L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
        />
      </Box>

      {/* Wave 2 */}
      <Box
        component={motion.svg}
        animate={{
          x: [0, 50, 0],
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: "linear",
        }}
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "200%",
          height: "100%",
        }}
        viewBox="0 0 1440 320"
        preserveAspectRatio="none"
      >
        <path
          fill="rgba(255, 255, 255, 0.08)"
          d="M0,224L48,213.3C96,203,192,181,288,181.3C384,181,480,203,576,213.3C672,224,768,224,864,208C960,192,1056,160,1152,154.7C1248,149,1344,171,1392,181.3L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
        />
      </Box>

      {/* Wave 3 */}
      <Box
        component={motion.svg}
        animate={{
          x: [0, -30, 0],
        }}
        transition={{
          duration: 25,
          repeat: Infinity,
          ease: "linear",
        }}
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "200%",
          height: "100%",
        }}
        viewBox="0 0 1440 320"
        preserveAspectRatio="none"
      >
        <path
          fill="rgba(255, 255, 255, 0.03)"
          d="M0,160L48,170.7C96,181,192,203,288,197.3C384,192,480,160,576,154.7C672,149,768,171,864,186.7C960,203,1056,213,1152,208C1248,203,1344,181,1392,170.7L1440,160L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
        />
      </Box>
    </Box>
  );
};

// Floating Particles Component
const FloatingParticles = () => {
  const particles = Array.from({ length: 20 });

  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        pointerEvents: "none",
      }}
    >
      {particles.map((_, i) => (
        <Box
          key={i}
          component={motion.div}
          animate={{
            y: [0, -100, 0],
            x: [0, Math.random() * 50 - 25, 0],
            opacity: [0.2, 0.5, 0.2],
          }}
          transition={{
            duration: 5 + Math.random() * 5,
            repeat: Infinity,
            delay: Math.random() * 5,
            ease: "easeInOut",
          }}
          sx={{
            position: "absolute",
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            width: "4px",
            height: "4px",
            borderRadius: "50%",
            backgroundColor: "rgba(255, 255, 255, 0.4)",
          }}
        />
      ))}
    </Box>
  );
};

// Count Up Animation Component
const CountUpStat = ({ end, suffix = "", duration = 2 }) => {
  const [count, setCount] = useState(0);
  const controls = useAnimation();

  useEffect(() => {
    let startTime;
    let animationFrame;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);

      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * end));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [end, duration]);

  return (
    <Typography
      component="span"
      sx={{
        fontSize: "inherit",
        fontWeight: "inherit",
        fontFamily: "inherit",
        color: "inherit",
      }}
    >
      {count}{suffix}
    </Typography>
  );
};

// Rotating Quote Component
const RotatingQuote = () => {
  const quotes = [
    {
      text: "NirogGyan's smart reports are phenomenal! They provide clear information and easy to understand insights.",
      author: "Dr. Arjun Dang",
      role: "Dr. Dangs Lab",
    },
    {
      text: "The patient-friendly offerings have become a highlight of our sales & marketing efforts.",
      author: "Dr. Danish",
      role: "CEO, Zaincare",
    },
    {
      text: "Smart Report is our special advantage to boost user-engagement with a great ROI.",
      author: "Ashwani",
      role: "PharmEasy",
    },
  ];

  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % quotes.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: { xs: "70px", sm: "75px", md: "80px", lg: "85px" },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {quotes.map((quote, index) => (
        <Box
          key={index}
          component={motion.div}
          initial={{ opacity: 0, x: 50 }}
          animate={{
            opacity: currentIndex === index ? 1 : 0,
            x: currentIndex === index ? 0 : -50,
            display: currentIndex === index ? "block" : "none",
          }}
          transition={{ duration: 0.5 }}
          sx={{
            position: "absolute",
            width: "100%",
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: "10px", sm: "11px", md: "12px", lg: "13px" },
              fontStyle: "italic",
              color: "#ffffff",
              mb: { xs: 0.75, sm: 1, md: 1.25, lg: 1.5 },
              lineHeight: 1.4,
              fontFamily: "'Poppins', sans-serif",
              px: { xs: 0.5, sm: 1, md: 0 },
            }}
          >
            "{quote.text}"
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: "9px", sm: "10px", md: "11px", lg: "12px" },
              fontWeight: 600,
              color: "#ffffff",
              fontFamily: "'Poppins', sans-serif",
            }}
          >
            {quote.author}
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: "8px", sm: "9px", md: "10px", lg: "11px" },
              color: "rgba(255, 255, 255, 0.85)",
              fontFamily: "'Poppins', sans-serif",
            }}
          >
            {quote.role}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

// Trust Badge Component
const TrustBadge = ({ icon: Icon, value, label }) => {
  return (
    <Box
      component={motion.div}
      whileHover={{ scale: 1.05 }}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: { xs: 0.4, sm: 0.5, md: 0.6, lg: 0.75 },
        p: { xs: 0.75, sm: 1, md: 1.25, lg: 1.5 },
        borderRadius: "12px",
        background: "rgba(255, 255, 255, 0.1)",
        backdropFilter: "blur(10px)",
        border: "1px solid rgba(255, 255, 255, 0.2)",
        transition: "all 0.3s ease",
        cursor: "pointer",
        "&:hover": {
          background: "rgba(255, 255, 255, 0.15)",
          borderColor: "rgba(255, 255, 255, 0.3)",
        },
      }}
    >
      <Icon size={22} color="#ffffff" strokeWidth={1.5} />
      <Typography
        sx={{
          fontSize: { xs: "16px", sm: "18px", md: "20px", lg: "22px" },
          fontWeight: 700,
          color: "#ffffff",
          fontFamily: "'Poppins', sans-serif",
          "& span": {
            color: "#ffffff !important",
          },
        }}
      >
        {value}
      </Typography>
      <Typography
        sx={{
          fontSize: { xs: "8px", sm: "9px", md: "10px", lg: "11px" },
          color: "rgba(255, 255, 255, 0.9)",
          fontFamily: "'Poppins', sans-serif",
          textAlign: "center",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

const HeroSection = () => {
  return (
    <Box
      component="section"
      sx={{
        position: "relative",
        height: { xs: "100%", md: "100vh" },
        display: "flex",
        alignItems: "center",
        overflow: "hidden",
        paddingTop: { xs: "70px", sm: "75px", md: "80px", lg: "85px" },
        paddingBottom: { xs: "30px", sm: "35px", md: "40px", lg: "45px" },
        background: "linear-gradient(135deg, #022674 0%, #1D47A4 40%, #3567D6 80%, #4674D9 100%)",
      }}
    >
      {/* Animated Wave Background */}
      <AnimatedWaves />

      {/* Floating Particles */}
      <FloatingParticles />

      {/* Floating Medical Icons */}
      <FloatingElement
        delay={0}
        duration={5}
        yOffset={30}
        rotateAmount={10}
        sx={{
          position: "absolute",
          top: "15%",
          left: "8%",
          opacity: 0.15,
          display: { xs: "none", md: "block" },
        }}
      >
        <Stethoscope size={60} color="#ffffff" strokeWidth={1.5} />
      </FloatingElement>

      <FloatingElement
        delay={1}
        duration={6}
        yOffset={35}
        rotateAmount={-12}
        sx={{
          position: "absolute",
          top: "25%",
          right: "10%",
          opacity: 0.15,
          display: { xs: "none", md: "block" },
        }}
      >
        <HeartPulse size={55} color="#ffffff" strokeWidth={1.5} />
      </FloatingElement>

      <FloatingElement
        delay={2}
        duration={5.5}
        yOffset={28}
        rotateAmount={8}
        sx={{
          position: "absolute",
          bottom: "20%",
          right: "12%",
          opacity: 0.12,
          display: { xs: "none", lg: "block" },
        }}
      >
        <Activity size={50} color="#ffffff" strokeWidth={1.5} />
      </FloatingElement>

      <FloatingElement
        delay={1.5}
        duration={6.5}
        yOffset={32}
        rotateAmount={-10}
        sx={{
          position: "absolute",
          bottom: "25%",
          left: "15%",
          opacity: 0.12,
          display: { xs: "none", lg: "block" },
        }}
      >
        <Award size={48} color="#ffffff" strokeWidth={1.5} />
      </FloatingElement>

      {/* Gradient Orbs */}
      <Box
        component={motion.div}
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        sx={{
          position: "absolute",
          top: "5%",
          right: "5%",
          width: { xs: "200px", sm: "250px", md: "300px", lg: "350px" },
          height: { xs: "200px", sm: "250px", md: "300px", lg: "350px" },
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(70, 116, 217, 0.25) 0%, transparent 70%)",
          filter: "blur(60px)",
          pointerEvents: "none",
        }}
      />

      <Box
        component={motion.div}
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.15, 0.3, 0.15],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 2,
        }}
        sx={{
          position: "absolute",
          bottom: "8%",
          left: "5%",
          width: { xs: "150px", sm: "180px", md: "250px", lg: "300px" },
          height: { xs: "150px", sm: "180px", md: "250px", lg: "300px" },
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(255, 255, 255, 0.12) 0%, transparent 70%)",
          filter: "blur(60px)",
          pointerEvents: "none",
        }}
      />

      <Container
        maxWidth="lg"
        sx={{
          position: "relative",
          zIndex: 10,
          px: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            maxWidth: "1000px",
            mx: "auto",
          }}
        >
          {/* Top Badge */}
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: { xs: 1.5, sm: 2, md: 2.5, lg: 3 },
              py: { xs: 0.5, sm: 0.6, md: 0.75, lg: 1 },
              borderRadius: "50px",
              background: "rgba(255, 255, 255, 0.15)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.25)",
              mb: { xs: 1.5, sm: 2, md: 2.5, lg: 3 },
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: "10px", sm: "11px", md: "12px", lg: "13px" },
                fontWeight: 600,
                color: "#ffffff",
                fontFamily: "'Poppins', sans-serif",
              }}
            >
              ✨ Trusted by <CountUpStat end={100} suffix="+" /> Healthcare Providers
            </Typography>
          </Box>

          {/* Main Heading with Gradient Text */}
          <Box
            component={motion.div}
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: {
                  staggerChildren: 0.1,
                  delayChildren: 0.2,
                },
              },
            }}
          >
            <Typography
              component={motion.h1}
              variants={fadeInUp}
              sx={{
                fontSize: { xs: "28px", sm: "38px", md: "48px", lg: "56px" },
                fontWeight: 800,
                lineHeight: 1.1,
                mb: { xs: 0.5, sm: 0.75, md: 1, lg: 1.25 },
                color: "#ffffff",
                fontFamily: "'Poppins', sans-serif",
                letterSpacing: { xs: "-0.5px", sm: "-1px", md: "-1.5px", lg: "-2px" },
                textShadow: "0 4px 30px rgba(0, 0, 0, 0.3)",
              }}
            >
              Real Stories, Real Impact
            </Typography>
          </Box>

          {/* Description */}
          <Box
            component={motion.div}
            initial="hidden"
            animate="visible"
            variants={{
              ...fadeInUp,
              visible: {
                ...fadeInUp.visible,
                transition: { duration: 0.6, delay: 0.4 },
              },
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: "12px", sm: "13px", md: "15px", lg: "16px" },
                color: "rgba(255, 255, 255, 0.9)",
                maxWidth: "800px",
                lineHeight: 1.5,
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 400,
                mb: { xs: 2, sm: 2.5, md: 3, lg: 3.5 },
                textShadow: "0 2px 15px rgba(0, 0, 0, 0.2)",
              }}
            >
              Transforming healthcare reporting with intelligent insights that empower
              hospitals, doctors, and patients worldwide
            </Typography>
          </Box>

          {/* Stats Row */}
          <Box
            component={motion.div}
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: {
                  staggerChildren: 0.15,
                  delayChildren: 0.6,
                },
              },
            }}
            sx={{ mb: { xs: 1.5, sm: 2, md: 2.5, lg: 3 }, width: "100%" }}
          >
            <Stack
              direction="row"
              spacing={{ xs: 0.75, sm: 1, md: 1.5 }}
              justifyContent="center"
              alignItems="stretch"
            >
              <Box component={motion.div} variants={fadeInUp} sx={{ flex: 1, maxWidth: { sm: "140px", md: "150px", lg: "160px" } }}>
                <TrustBadge
                  icon={Users}
                  value={<CountUpStat end={1} suffix="M+" />}
                  label="Reports Generated"
                />
              </Box>
              <Box component={motion.div} variants={fadeInUp} sx={{ flex: 1, maxWidth: { sm: "140px", md: "150px", lg: "160px" } }}>
                <TrustBadge
                  icon={Award}
                  value={<CountUpStat end={95} suffix="%" />}
                  label="Satisfaction Rate"
                />
              </Box>
              <Box component={motion.div} variants={fadeInUp} sx={{ flex: 1, maxWidth: { sm: "140px", md: "150px", lg: "160px" } }}>
                <TrustBadge
                  icon={TrendingUp}
                  value={<CountUpStat end={40} suffix="%" />}
                  label="Time Saved"
                />
              </Box>
            </Stack>
          </Box>

          {/* Rotating Quote Carousel */}
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            sx={{
              width: "100%",
              maxWidth: { xs: "90%", sm: "85%", md: "650px", lg: "700px" },
              p: { xs: 1.25, sm: 1.5, md: 2, lg: 2.5 },
              borderRadius: "16px",
              background: "rgba(255, 255, 255, 0.08)",
              backdropFilter: "blur(12px)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
            }}
          >
            <RotatingQuote />
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default HeroSection;